import {useState} from 'react';
import {useToast} from '@chakra-ui/react';
import {useEnqueuePlayoffGameMutation} from 'app/api';
import {EnqueuePlayoffGameRequest, GameView} from 'types/dto';
import {
  DEFAULT_GAME_DURATION_MINUTES,
  START_TIME_ROUNDING_MINUTES, TOAST_DURATION_LONG,
  TOAST_DURATION_MEDIUM,
  TOAST_DURATION_SHORT
} from '../playoffSchedulingConstants';
import {formatDateTimeLocal, getRoundedStartTime, validateTimes} from '../../../../utils/PlayoffUtils';

interface Court {
  id: number;
  name: string;
}

interface UseScheduleGameFormProps {
  eventId: number;
  courts?: Court[];
  selectedLeague?: number;
  onSuccess: (gameData: GameView) => void;
}

interface UseScheduleGameFormReturn {
  selectedLeagueForSchedule: number | undefined;
  setSelectedLeagueForSchedule: (leagueId: number | undefined) => void;
  selectedCourtId: number | undefined;
  setSelectedCourtId: (courtId: number | undefined) => void;
  startTime: string;
  setStartTime: (time: string) => void;
  endTime: string;
  setEndTime: (time: string) => void;
  timeError: string;
  isEnqueueing: boolean;
  initializeForm: () => void;
  handleStartTimeChange: (value: string) => void;
  handleEndTimeChange: (value: string) => void;
  handleSubmit: () => Promise<void>;
  resetForm: () => void;
}

export const useScheduleGameForm = ({
                                      eventId,
                                      courts,
                                      selectedLeague,
                                      onSuccess,
                                    }: UseScheduleGameFormProps): UseScheduleGameFormReturn => {
  const toast = useToast();
  const [enqueuePlayoffGame, {isLoading: isEnqueueing}] = useEnqueuePlayoffGameMutation();

  const [selectedLeagueForSchedule, setSelectedLeagueForSchedule] = useState<number | undefined>();
  const [selectedCourtId, setSelectedCourtId] = useState<number | undefined>();
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [timeError, setTimeError] = useState('');

  const initializeForm = () => {
    if (selectedLeague) {
      setSelectedLeagueForSchedule(selectedLeague);
    }

    if (courts && courts.length > 0) {
      setSelectedCourtId(courts[0].id);
    }

    const now = getRoundedStartTime(START_TIME_ROUNDING_MINUTES);
    setStartTime(formatDateTimeLocal(now));

    const endDate = new Date(now.getTime() + DEFAULT_GAME_DURATION_MINUTES * 60 * 1000);
    setEndTime(formatDateTimeLocal(endDate));

    setTimeError('');
  };

  const handleStartTimeChange = (value: string) => {
    setStartTime(value);
    if (endTime) {
      const startDate = new Date(value);
      const endDate = new Date(endTime);
      if (endDate <= startDate) {
        const newEndDate = new Date(startDate.getTime() + DEFAULT_GAME_DURATION_MINUTES * 60 * 1000);
        setEndTime(formatDateTimeLocal(newEndDate));
      }
    }
    setTimeError('');
  };

  const handleEndTimeChange = (value: string) => {
    setEndTime(value);
    setTimeError('');
  };

  const handleSubmit = async (): Promise<void> => {
    if (!selectedLeagueForSchedule || !selectedCourtId) {
      toast({
        title: 'Missing Information',
        description: 'Please select a league and court',
        status: 'warning',
        duration: TOAST_DURATION_SHORT,
        isClosable: true,
      });
      return;
    }

    const validation = validateTimes(startTime, endTime);
    if (!validation.isValid) {
      setTimeError(validation.error || 'Invalid time selection');
      return;
    }

    try {
      const enqueuePlayoffGameRequest: EnqueuePlayoffGameRequest = {
        eventId,
        leagueId: selectedLeagueForSchedule,
        courtId: selectedCourtId,
        startTime: new Date(startTime).toISOString(),
        endTime: new Date(endTime).toISOString(),
      };

      const response = await enqueuePlayoffGame({
        eventId,
        leagueId: selectedLeagueForSchedule,
        enqueuePlayoffGameRequest,
      }).unwrap();

      const courtName = courts?.find((c) => c.id === selectedCourtId)?.name || 'selected court';

      toast({
        title: 'Game Scheduled Successfully',
        description: `Playoff game scheduled on ${courtName}`,
        status: 'success',
        duration: TOAST_DURATION_MEDIUM,
        isClosable: true,
      });

      if (response?.id) {
        onSuccess(response);
      }

      resetForm();
    } catch (error: any) {
      const errorMessage =
        error?.data?.message || error?.message || 'An error occurred while scheduling the game';
      toast({
        title: 'Failed to Schedule Game',
        description: errorMessage,
        status: 'error',
        duration: TOAST_DURATION_LONG,
        isClosable: true,
      });
      throw error;
    }
  };

  const resetForm = () => {
    setSelectedLeagueForSchedule(undefined);
    setSelectedCourtId(undefined);
    setStartTime('');
    setEndTime('');
    setTimeError('');
  };

  return {
    selectedLeagueForSchedule,
    setSelectedLeagueForSchedule,
    selectedCourtId,
    setSelectedCourtId,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    timeError,
    isEnqueueing,
    initializeForm,
    handleStartTimeChange,
    handleEndTimeChange,
    handleSubmit,
    resetForm,
  };
};
